import '../style/applicationsAndPolicies.css';
import '../style/dots.css';
import moment from "moment";
import {
	LEGAL_ADVICE_TEXT,
	POLICYHOLDER_TEXT,
	UI_LIB_TABLE_VIEW_OPTIONS,
} from '../helpers/variables';
import {Table, FormGroup, Button, HrefLink, HorizontalRule, Heading} from 'arag-ui-lib';
import { DOTTED, HEADING_2, MOMENT_DATE_FORMAT, POLICY_STATUS_MN_REFERRED, PRIMARY, QUATERNARY, RIGHT, formatGlobalSearchOptionLabel } from '../helpers/constants';
import { formatSolicitorTableLabel } from '../helpers/functions';
import CONTENT_STRINGS from '../helpers/contentStrings';
import { FORM_GROUP_VARIANTS } from '../helpers/formGroupVariants';
import useFeatureFlag from '../hooks/useFeatureFlag';
import SolicitorFirmSearchSelect from './solicitorFirmSearchSelect';
import { FEATURE_FLAGS } from '../constants/configurationKeys';


const commonStyle = { whiteSpace: 'normal', wordWrap: 'break-word' };

const applyCommonStyle = (columns) => columns.map(column => ({
	...column,
	style: { ...commonStyle, ...(column.style || {}) }
}));

const agentTableColumns = [
	{propertyName: "solicitorReference", header: `${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.REFERENCE}`},
	{propertyName: "aragReference", header: CONTENT_STRINGS.ARAG_REFERENCE, formatter: (row) => <HrefLink to={`/application-and-policies/application-policy/${row.id}`} label={row.aragReference}/>},
	{propertyName: "clientName", header: `${POLICYHOLDER_TEXT} ${CONTENT_STRINGS.NAME}`},
	{propertyName: "caseTypeName", header: CONTENT_STRINGS.CASE_TYPE},
	{propertyName: "subCaseTypeName", header: CONTENT_STRINGS.SUB_CASE_TYPE},
	{propertyName: "date", header: CONTENT_STRINGS.SUBMISSION_DATE, formatter: (row) => row.submissionDate ? moment(row.submissionDate).format(MOMENT_DATE_FORMAT) : ''},
	{propertyName: "policyStatus", header: CONTENT_STRINGS.APPLICATION_STATUS}
];

const userUiLibTableColumns = [
	{propertyName: "solicitorReference", header: `${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.REFERENCE}`},
	{propertyName: "aragReference", header: CONTENT_STRINGS.ARAG_REFERENCE, formatter: (row) => <HrefLink to={`/application-and-policies/application-policy/${row.id}`} label={row.aragReference}/>},
	{propertyName: "solicitorFirm", header: `${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.FIRM}`, formatter: (row) => formatSolicitorTableLabel({label: row.solicitorFirm, aragProvider: row.aragProvider})},
	{propertyName: "clientName", header: `${POLICYHOLDER_TEXT} ${CONTENT_STRINGS.NAME}`},
	{propertyName: "caseTypeName", header: CONTENT_STRINGS.CASE_TYPE},
	{propertyName: "subCaseTypeName", header: CONTENT_STRINGS.SUB_CASE_TYPE},
	{propertyName: "date", header: CONTENT_STRINGS.SUBMISSION_DATE, formatter: (row) => row.submissionDate ? moment(row.submissionDate).format(MOMENT_DATE_FORMAT) : ''},
	{propertyName: "policyStatus", header: CONTENT_STRINGS.APPLICATION_STATUS, formatter: (row) => row.policyStatusMachineName === POLICY_STATUS_MN_REFERRED ? row.policyStatus + CONTENT_STRINGS.REFERRED_TO + row.referredToName : row.policyStatus},
	{propertyName: "agentName", header: CONTENT_STRINGS.CREATED_BY},
	{propertyName: "readByArag", header: CONTENT_STRINGS.READ_BY_ARAG, formatter: (row) => row.readByArag ? CONTENT_STRINGS.READ : CONTENT_STRINGS.UNREAD},
];

const ApplicationsAndPolicies = (props) => {
	const {
		solicitorUsers,
		HandleInputChange,
		Store,
		isAgentAllowedToSeeAllFirmApplications,
		handleSolicitorUsersFirmsFilterChange,
		handleSolicitorFirmsFilterChange,
		getSolicitorUsersForMultipleSelect,
		handleStatusesFilterChange,
		caseTypesOptions,
		handleCaseTypesFilterChange,
		subCaseTypesOptions,
		handleSubCaseTypesFilterChange,
		handleFilterClick,
		clearApplicationsAndPoliciesFilters,
		onPageSizeChange,
		goAtPrevious,
		goAtNext,
		goAtStart,
		goAtEnd,
		goAtPage,
		HandleAutosuggestButton,
		onSuggestionSelected,
		isArag,
		isSolicitor,
		push,
		suggestionOptions,
		onChange,
		featureFlagGlobalPolicySearchEnabled,
		options,
		isSearchLoading
	} = props;

	const columns = isArag
		? applyCommonStyle(userUiLibTableColumns)
		: applyCommonStyle(agentTableColumns);

	const solicitorUserColleaguesOptions = solicitorUsers.map(user => ({
		value: user.userId,
		label: `${user.forename} ${user.surname}`
	}));

	return (
		<div className="row">
			<div className="col-sm-12 applicationsAndPolicies-wrapper">
				<Heading
					variant={HEADING_2}
					title={CONTENT_STRINGS.APPLICATIONS_POLICIES_TITLE}
					headingTestId={'application-list-header-text'}
				/>
				{
					isArag 
					?
						<Button
							label={CONTENT_STRINGS.CREATE_NEW_APPLICATION}
							variant={QUATERNARY}
							onClick={() => push("/new-application")}
							buttonTestId={'aplication-list-create-new'}
						/>
					:
						null
				}
				<form
					className="form-horizontal applicationsAndPolicies-top-wrapper"
					onSubmit={evt => {
						evt.preventDefault();
						HandleInputChange('selectedSuggestions', Store.suggestions);
					}}
					data-testid={"application-list-filters"}
				>
					{
						featureFlagGlobalPolicySearchEnabled
						?
							<>
								<FormGroup
									labelSize={"col-sm-3"}
									inputSize={"col-sm-7"}
									label={CONTENT_STRINGS.GLOBAL_POLICY_SEARCH}
									variant={FORM_GROUP_VARIANTS.SEARCHABLE_LIST}
									inputProps={{
										options: props.globalSearchOptions,
										onChange: props.onGlobalSearchSelected,
										onInputChange: props.onGlobalSearchInputChange,
										inputValue: props.Store.globalSearchValue || '',
										value: null,
										instanceId: 'globalPolicySearch',
										placeholder: CONTENT_STRINGS.GLOBAL_POLICY_SEARCH_PLACEHOLDER,
										noOptionsText: props.Store.globalSearchError ? CONTENT_STRINGS.SEARCH_FAILED : (props.Store.globalSearchValue || '').length >= 5 ? CONTENT_STRINGS.NO_RESULTS_FOUND : CONTENT_STRINGS.MIN_5_CHARACTERS_TO_SEARCH,
										onSearchIconClick: props.onGlobalSearchIconClick,
										isLoading: props.Store.globalSearchIsLoading,
										filterOption: () => true,
										formatOptionLabel: formatGlobalSearchOptionLabel
									}}
									formGroupTestId={"global-policy-search-form-group"}
									infoText={CONTENT_STRINGS.GLOBAL_POLICY_SEARCH_INFO}
								/>
								<HorizontalRule variant={DOTTED} />
							</>
						:
							null
					}
					{
						isSolicitor && isAgentAllowedToSeeAllFirmApplications
						?
							<FormGroup
								labelSize={"col-sm-3"}
								inputSize={"col-sm-7"}
								label={CONTENT_STRINGS.FIRM_AGENTS}
								variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
								inputProps={{
									instanceId: "firmAgentsMultipleSelectInstanceId",
									options: solicitorUserColleaguesOptions,
									onChange: handleSolicitorUsersFirmsFilterChange,
									value: Store.selectedSolicitorUsers,
									placeholder: CONTENT_STRINGS.QUICK_SEARCH
								}}
								formGroupTestId={"application-list-filter-firm-agents"}
							/>
						:
							null
					}
					{
						isArag
						?
							<>
								<SolicitorFirmSearchSelect
									labelSize={"col-sm-3"}
									inputSize={"col-sm-7"}
									label={`${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.FIRMS}`}
									instanceId={"solicitorFirmsMultipleSelectInstanceId"}
									isMulti={true}
									onChange={handleSolicitorFirmsFilterChange}
									value={Store.selectedSolicitorFirms}
									formGroupTestId={"application-list-filter-solicitor-firms"}
								/>
								<FormGroup
									labelSize={"col-sm-3"}
									inputSize={"col-sm-7"}	
									label={`${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.USERS}`}
									variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
									inputProps={{
										instanceId: "solicitorUsersMultipleSelectInstanceId",
										options: getSolicitorUsersForMultipleSelect(),
										onChange: handleSolicitorUsersFirmsFilterChange,
										value: Store.selectedSolicitorUsers,
										placeholder: CONTENT_STRINGS.QUICK_SEARCH
									}}
									formGroupTestId={"application-list-filter-solicitor-users"}
								/>
							</>
						:
							null
					}
					<FormGroup
						labelSize={"col-sm-3"}
						inputSize={"col-sm-7"}
						label={CONTENT_STRINGS.STATUSES}
						variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
						inputProps={{
							instanceId: "statusesMultipleSelectInstanceId",
							options: options,
							onChange: handleStatusesFilterChange,
							value: Store.selectedStatuses,
							placeholder: CONTENT_STRINGS.QUICK_SEARCH
						}}
						formGroupTestId={"application-list-filter-statuses"}
					/>
					<FormGroup
						labelSize={"col-sm-3"}
						inputSize={"col-sm-7"}
						label={CONTENT_STRINGS.CASE_TYPES}
						variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
						inputProps={{
							instanceId: "caseTypesMultipleSelectInstanceId",
							options: caseTypesOptions,
							onChange: handleCaseTypesFilterChange,
							value: Store.selectedCaseTypes,
							placeholder: CONTENT_STRINGS.QUICK_SEARCH
						}}
						formGroupTestId={"application-list-filter-case-types"}
					/>
					<FormGroup
						labelSize={"col-sm-3"}
						inputSize={"col-sm-7"}
						label={CONTENT_STRINGS.SUB_CASE_TYPES}
						variant={FORM_GROUP_VARIANTS.MULTI_SELECT}
						inputProps={{
							instanceId: "subCaseTypesMultipleSelectInstanceId",
							options: subCaseTypesOptions,
							onChange: handleSubCaseTypesFilterChange,
							value: Store.selectedSubCaseTypes,
							placeholder: CONTENT_STRINGS.QUICK_SEARCH
						}}
						formGroupTestId={"application-list-filter-sub-case-types"}
					/>
					<FormGroup
						labelSize={"col-sm-3"}
						inputSize={"col-sm-7"}
						label={CONTENT_STRINGS.SEARCH}
						variant={FORM_GROUP_VARIANTS.SEARCHABLE_LIST}
						inputProps={{
							options: suggestionOptions,
							onChange: onSuggestionSelected,
							onInputChange: onChange,
							inputValue: Store.value,
							value: null,
							placeholder: ' ',
							instanceId: 'applicationSearch',
							noOptionsText: CONTENT_STRINGS.NO_RESULTS_FOUND,
							onSearchIconClick: HandleAutosuggestButton,
							isLoading: isSearchLoading
						}}
						formGroupTestId={'application-list-filter-auto-suggest'}
					/>
					<div className="row col-sm-12">
						<div className="col-sm-3"></div>
						{
							isArag
							?
								<FormGroup
									labelSize={"col-sm-3"}
									inputSize={"col-sm-6"}
									label={CONTENT_STRINGS.DISPLAY_DRAFT_APPLICATIONS}
									variant={FORM_GROUP_VARIANTS.CHECKBOX_INLINE}
									inputProps={{
										name: "includeDraft",
										onChange: (evt) => HandleInputChange(evt.target.name, !Store.includeDraft),
										checked: Store.includeDraft,
										alignLabelBottom: true
									}}
									formGroupTestId={'display-draft-control'}
								/>
							:
								null
						}
					</div>
					<div className="form-group">
						<div className="col-sm-6 col-sm-offset-3">
							<Button
								variant={PRIMARY}
								label={CONTENT_STRINGS.FILTER_LABEL}
								arrowDirection={RIGHT}
								onClick={handleFilterClick}
								style={{ marginRight: '10px' }}
								buttonTestId={"button-filter"}
							/>
							<Button
								variant={QUATERNARY}
								label={CONTENT_STRINGS.CLEAR_FILTERS_LABEL}
								arrowDirection={RIGHT}
								onClick={clearApplicationsAndPoliciesFilters}
								buttonTestId={"button-clear-filters"}
							/>
						</div>
					</div>
				</form>
				<Table
					striped
					loading={Store.fetching || Store.applications === null}
					columns={columns}
					data={Store.applications}
					pageSize={parseInt(Store.selectedView)}
					totalRecords={Store.totalRecords}
					totalPages={Store.totalBoxes}
					page={Store.page}
					hasActions={isSolicitor}
					actions={[
						{
							key: 'details',
							label: CONTENT_STRINGS.DETAILS,
							callback: (row) => "/application-and-policies/application-policy/" + row.id,
							isActionLink: true,
							arrowDirection: RIGHT
						},
					]}
					pageSizeOptions={UI_LIB_TABLE_VIEW_OPTIONS}
					onPageSizeChange={onPageSizeChange}
					goAtPrevious={goAtPrevious}
					goAtNext={goAtNext}
					goAtStart={goAtStart}
					goAtEnd={goAtEnd}
					goAtPage={goAtPage}
					withVerticalAlignedHeader
					tableWrapperTestId={"applications-policies-table"}
					noResultsMessage={CONTENT_STRINGS.NO_APPLICATION_TO_SHOW}
				/>
			</div>
		</div>
	);
};

export default ApplicationsAndPolicies;
